// React and core dependencies
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// Material-UI components and hooks
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import WrongLocationOutlined from "@mui/icons-material/WrongLocationOutlined";

// Kilroy MUI components and utilities
import {
  CustomAnimation,
  FlightCardDesktop,
  FlightCardMobile,
  CtaDialog,
  IItinerary,
  MediaQuery,
  USPslider,
  useConfig,
  dateObjToDateStringDotted,
} from "@thekilroygroup/shared-ui";

// Custom hooks
import useQueryParams, { IQueryParams } from "hooks/useQueryParams";
import { useTranslations } from "hooks/useTranslations";

// Redux store and actions
import { UseAppDispatch } from "redux/store";
import {
  setSearchForm,
  ISearchForm,
  searchFormSelector,
  IFlightDates,
  changeFlights,
} from "redux/searchForm";
import { updateSearchQuery } from "redux/searchQuery";
import {
  setItineraryFromSearch,
  setSelectedFareId,
} from "redux/selectedItinerary";
import { useLazyRevalidateQuery } from "redux/services/revalidate";

// Components
import { OneWayCalendar } from "components/LowPriceCalendar/OneWayCalendar/OneWayCalendar";
import { YouthStudentSwitch } from "components/LowPriceCalendar/YouthStudentSwitch";
import { RevalidateHandler } from "components/ResponsePopup/RevalidateHandler";
import {
  MobileHeader,
  MobileLowPriceHeader,
  DesktopHeader,
  DesktopLowPriceHeader,
} from "components/Header";
import { MetapartnerTracking } from "components/MetapartnerTracking/MetapartnerTracking";
import { buildQueryString, getShareUrl } from "utils/queryStrings";
import {
  getAirlineNames,
  getAirportName,
  getCity,
} from "utils/flightDetailApiUtils";
import { getFlightCardTranslations } from "utils/translationsUtils";

// Validation and interfaces
import { IAppConfigObject } from "appConfig.interfaces";

// Animations and styles
import {
  backgroundAnimations,
  discountTextBoxAnimations,
  flightCardAnimations,
  lowPriceCalendarAnimations,
  navigateDelay,
  searchHeaderAnimations,
  titleBoxAnimations,
  youthStudentSwitchAnimations,
} from "./animations.LowPriceCalendarPage";
import { sx } from "./styles";
import { ReturnCalendarWrapper } from "components/LowPriceCalendar/ReturnCalendar/ReturnCalendarWrapper";
import { validateSearchForm } from "validation/validateSearch";
import { getSelectedItineraryDates } from "components/LowPriceCalendar/methods";
import TravelDates from "components/Search/TravelDates/TravelDates";
import { ISearchFormValidation } from "validation/validation.interfaces";
const LowPriceCalendarPage = () => {
  const dispatch = UseAppDispatch();
  const params: IQueryParams = useQueryParams();

  const searchQuery = params.searchQuery;
  const { isValid } = validateSearchForm(searchQuery.flights);
  const [pageIsMounted, setPageIsMounted] = useState(false);
  const searchForm: ISearchForm = useSelector(searchFormSelector);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { locale, appConfig } = useConfig<IAppConfigObject>();
  const [showError, setShowError] = useState(false);
  const [showYouthStudentItineraries, setShowYouthStudentItineraries] =
    useState(true);
  const [selectedItinerary, setSelectedItinerary] =
    useState<IItinerary | null>();
  const [selectedItineraryIsUpdated, setSelectedItineraryIsUpdated] =
    useState(false);
  const [customErrorCodes, setCustomErrorCodes] = useState<string[]>([]);

  const { nextFlights: flights } = validateSearchForm(
    params.searchQuery.flights,
  );

  const flightCardDesktopRef = useRef<HTMLDivElement>(null);
  const flightCardMobileRef = useRef<HTMLDivElement>(null);

  const getAirportString = () =>
    searchQuery?.flights[0]?.departure
      ? `${getAirportName(
          searchQuery.flights[0].departure as string,
        )} - ${getAirportName(searchQuery.flights[0].arrival as string)}`
      : "";

  const navigate = useNavigate();
  const navigateToSearchPage = () => {
    navigate(`/search-results?${buildQueryString(searchForm)}`);
  };

  const [triggerRevalidate, revalidateResult] = useLazyRevalidateQuery();
  const [showRevalidateHandler, setShowRevalidateHandler] = useState(false);

  const chooseItinerary = (id: string) => {
    setShowRevalidateHandler(true);
    if (selectedItinerary) {
      dispatch(setItineraryFromSearch(selectedItinerary));
      triggerRevalidate({
        itinerary: selectedItinerary,
        searchQuery,
        searchRequestId: searchForm.searchRequestId,
        config: appConfig,
      });
    }
  };

  const confirmAndNavigateTo = (path: string) => {
    setPageIsMounted(false);
    setTimeout(() => {
      navigate(path);
    }, navigateDelay);
  };

  const {
    translations,
    translations: {
      customResponseCodes,
      lowPriceCalendar: { title, youthStudentSwitchLabel },
      searchResults: { textBoxContent },
    },
  } = useTranslations();

  const flightCardTranslations = getFlightCardTranslations(translations);

  // Shared props for both FlightCard components
  const sharedFlightCardProps = {
    ...flightCardTranslations,
    airlineLogosPath: `${appConfig.SHARED_ASSETS_BASE_URL}/airline-logos`,
    locale,
    title: translations.pageTitle,
    baggageTypesTranslations:
      flightCardTranslations.baggageInfoTranslations.baggageTypes,
    passengerTypesTranslations:
      flightCardTranslations.baggageInfoTranslations.passengerTypes,
    passengerTypesMapTranslations: flightCardTranslations.passengerTypesMap,
    itinerary: selectedItinerary as IItinerary,
    handleChooseItinerary: (id: string) => chooseItinerary(id),
    getAirportName,
    getAirlineNames,
    getCity,
  };

  /**
   * Change dates of a flight
   */
  const changeDates = (nextDates: IFlightDates, index: number) => {
    let nextFlights = [...flights];
    nextFlights[index] = {
      ...nextFlights[index],
      departureDate: dateObjToDateStringDotted(nextDates.departureDate),
    };

    nextFlights[1] = {
      ...nextFlights[1],
      departureDate:
        nextDates.returnDate !== null
          ? dateObjToDateStringDotted(nextDates.returnDate)
          : null,
    };
    dispatch(changeFlights(nextFlights));
  };

  const initFrontPage = () => {
    window.scrollTo(0, 0);
    dispatch(setSearchForm(params.searchQuery));
    dispatch(setSelectedFareId(params.fareId));
    dispatch(updateSearchQuery(params.searchQuery));
    const { isValid, nextFlights }: ISearchFormValidation =
      validateSearchForm(flights);

    if (isValid) {
    } else if (
      nextFlights.some(
        (flight) => flight.validation.departureDate.valid === false,
      )
    ) {
      setCustomErrorCodes(["noReturnDate"]);
      return;
    } else {
      setShowError(true);
      return;
    }
    setPageIsMounted(true);
  };

  useEffect(() => {
    initFrontPage();
  }, []);

  const isInView = (element: HTMLDivElement) => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight)
    );
  };

  useEffect(() => {
    if (selectedItinerary !== undefined) {
      const flightCardDesktop = flightCardDesktopRef.current;
      const flightCardMobile = flightCardMobileRef.current;

      if (isDesktop && flightCardDesktop && !isInView(flightCardDesktop)) {
        flightCardDesktop?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      } else if (
        !isDesktop &&
        flightCardMobile &&
        !isInView(flightCardMobile)
      ) {
        flightCardMobile?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
    }
  }, [selectedItinerary]);

  return (
    <>
      <Dialog
        maxWidth="xs"
        open={customErrorCodes.length > 0}
        sx={{ zIndex: theme.zIndex.drawer - 1 }}
      >
        {translations.customResponseCodes && (
          <DialogContent>
            <Typography variant="h3" sx={{ mb: 2 }}>
              {translations.customResponseCodes["noReturnDate"]?.title}
            </Typography>
            <TravelDates
              index={0}
              flights={flights}
              onDateChange={(dates, index) => {
                changeDates(dates, index);
              }}
            />
            <Typography variant="bodyMedium" sx={{ mt: 1 }}>
              {translations.customResponseCodes["noReturnDate"]?.text}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setCustomErrorCodes([]);
                initFrontPage();
              }}
              sx={{ mt: 2, width: "100%" }}
            >
              {translations.customResponseCodes["noReturnDate"].CTA}
            </Button>
          </DialogContent>
        )}
      </Dialog>
      <CustomAnimation
        in={pageIsMounted}
        {...backgroundAnimations}
        sx={{ overflow: "visible" }}
      >
        <Box className="lowprice-calendar-page-wrapper" sx={sx.pageWrapper}>
          {searchQuery?.flights[0]?.departure && (
            <>
              <MediaQuery max="md">
                <MobileHeader
                  top={
                    <MobileLowPriceHeader
                      navigateToSearchPage={() => navigateToSearchPage()}
                    />
                  }
                />
              </MediaQuery>
              <MediaQuery min="md">
                <CustomAnimation in={pageIsMounted} {...searchHeaderAnimations}>
                  <DesktopHeader>
                    <DesktopLowPriceHeader
                      navigateToSearchPage={() => navigateToSearchPage()}
                    />
                  </DesktopHeader>
                </CustomAnimation>
              </MediaQuery>
              {isValid && (
                <Container sx={sx.container}>
                  <CustomAnimation
                    in={pageIsMounted}
                    {...youthStudentSwitchAnimations}
                    style={{ backgroundColor: "rgba(0, 0, 0, 0)" }}
                  >
                    <YouthStudentSwitch
                      label={youthStudentSwitchLabel}
                      onChange={() =>
                        setShowYouthStudentItineraries(
                          !showYouthStudentItineraries,
                        )
                      }
                      checked={showYouthStudentItineraries}
                    />
                  </CustomAnimation>

                  <Box sx={sx.topWrapper}>
                    <CustomAnimation
                      in={pageIsMounted}
                      style={{ transformOrigin: "50% 0 0", flex: 1 }}
                      {...titleBoxAnimations}
                      sx={{ display: { xs: "none", sm: "initial" } }}
                    >
                      <Box sx={sx.titleWrapper}>
                        <Typography
                          variant="titleMedium"
                          weight="bold"
                          sx={sx.subTitle}
                        >
                          {title}
                        </Typography>
                        <Typography variant="h3">
                          {getAirportString()}
                        </Typography>
                      </Box>
                    </CustomAnimation>
                    {textBoxContent.length > 0 && (
                      <Box sx={{ flex: 1 }}>
                        <CustomAnimation
                          in={pageIsMounted}
                          style={{ transformOrigin: "50% 0 0", flex: 1 }}
                          {...discountTextBoxAnimations}
                        >
                          {textBoxContent !== "textBoxContent" && (
                            <Alert
                              icon={false}
                              variant="neutral"
                              sx={sx.alertWrapper}
                            >
                              {textBoxContent}
                            </Alert>
                          )}
                        </CustomAnimation>
                      </Box>
                    )}
                  </Box>

                  {searchForm.tripType === "ONE_WAY" && (
                    <CustomAnimation
                      in={pageIsMounted && !showError}
                      {...lowPriceCalendarAnimations}
                      sx={{ overflow: "visible" }}
                    >
                      <OneWayCalendar
                        setSelectedItinerary={(itinerary: IItinerary) =>
                          setSelectedItinerary(itinerary)
                        }
                        selectedItinerary={selectedItinerary}
                        showYouthStudentItineraries={
                          showYouthStudentItineraries
                        }
                      />
                    </CustomAnimation>
                  )}
                  {searchQuery.tripType === "RETURN" && (
                    <CustomAnimation
                      in={pageIsMounted && !showError}
                      {...lowPriceCalendarAnimations}
                      sx={{ overflow: "visible" }}
                    >
                      <ReturnCalendarWrapper
                        selectedItineraryId={selectedItinerary?.id || null}
                        setSelectedItinerary={(itinerary: IItinerary) =>
                          setSelectedItinerary(itinerary)
                        }
                        showYouthStudentItineraries={
                          showYouthStudentItineraries
                        }
                        setSelectedItineraryIsUpdated={(isUpdated: boolean) =>
                          setSelectedItineraryIsUpdated(isUpdated)
                        }
                        navigateToSearchPage={() => navigateToSearchPage()}
                        flights={flights}
                        setCustomErrorCodes={(codes: string[]) =>
                          setCustomErrorCodes(codes)
                        }
                        selectedItineraryDates={getSelectedItineraryDates(
                          selectedItinerary,
                        )}
                      />
                    </CustomAnimation>
                  )}
                  {isDesktop && (
                    <>
                      <Box sx={{ pb: 2 }} ref={flightCardDesktopRef}>
                        {selectedItinerary && (
                          <Box
                            key={selectedItinerary.id}
                            sx={sx.flightCardDesktop}
                          >
                            <CustomAnimation
                              in={pageIsMounted}
                              {...flightCardAnimations}
                              sx={{ overflow: "visible" }}
                            >
                              <FlightCardDesktop
                                {...sharedFlightCardProps}
                                key={selectedItinerary.id}
                                isSelected={false}
                              />
                            </CustomAnimation>
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <USPslider
                          usps={translations.usp}
                          iconColor={theme.palette.primary.main}
                          pagination
                        />
                      </Box>
                    </>
                  )}

                  {!isDesktop && (
                    <Box sx={{ pb: 2 }} ref={flightCardMobileRef}>
                      {selectedItinerary && (
                        <Box key={selectedItinerary.id}>
                          <CustomAnimation
                            in={pageIsMounted}
                            {...flightCardAnimations}
                            sx={{ overflow: "visible" }}
                          >
                            <FlightCardMobile
                              {...sharedFlightCardProps}
                              shareUrl={getShareUrl(
                                searchQuery,
                                false,
                                selectedItinerary.id,
                              )}
                            />
                          </CustomAnimation>
                        </Box>
                      )}
                    </Box>
                  )}
                </Container>
              )}
            </>
          )}
        </Box>
      </CustomAnimation>

      {showRevalidateHandler && (
        <RevalidateHandler
          revalidateResult={revalidateResult}
          setIsRevalidated={setShowRevalidateHandler}
          confirmAndNavigateTo={(path) => confirmAndNavigateTo(path)}
        />
      )}
      {showError && (
        <CtaDialog
          {...customResponseCodes["4007"]}
          onClose={() => {
            navigateToSearchPage();
          }}
          ctaAction={() => {
            navigateToSearchPage();
          }}
          icon={<WrongLocationOutlined />}
        />
      )}

      {selectedItinerary &&
        selectedItineraryIsUpdated &&
        params.searchQuery.utm_source && (
          <MetapartnerTracking
            selectedItinerary={selectedItinerary}
            eventType="pre-conversion"
            createdDate={new Date().toISOString()}
            utm_source={params.searchQuery.utm_source}
            conversion_id=""
            searchQuery={params.searchQuery}
            price={
              params.searchQuery.totalPrice ||
              selectedItinerary?.totalFareAmounts.total ||
              0
            }
          />
        )}
    </>
  );
};
export default LowPriceCalendarPage;
